import { z } from "zod";
import {
  ALLOWANCE_PERIODS,
  BILLING_INTERVALS,
  PAID_TIERS,
  PLAN_TIERS,
  SUBSCRIPTION_SOURCES,
  SUBSCRIPTION_STATUSES,
  type BillingInterval,
  type PaidTier,
} from "#src/types/entitlements.ts";

/**
 * API shapes for buying and viewing a plan. The vault's own shapes live in
 * `wallet.ts`; these are the surfaces that move a user between tiers.
 *
 * Timestamps cross this boundary as ISO strings rather than `Date`, because
 * JSON has no date type and a silently-stringified `Date` types as one on the
 * client while being a string at runtime.
 */

/**
 * The statsig gate that arms the whole billing rollout: it reveals the
 * Settings → Billing tab on the client AND arms server-side unit enforcement,
 * so the cohort that can see billing UI is exactly the cohort subject to
 * refusals. One knob, one rollout.
 */
export const BILLING_GATE = "billing_ui";

export const billingInterval = z.enum(BILLING_INTERVALS);

/**
 * `interval` is optional so web builds that predate annual billing keep
 * working; the server reads an omitted one as monthly.
 */
export const checkoutReq = z.object({
  tier: z.enum(PAID_TIERS),
  interval: billingInterval.optional(),
});

/** Refused because the tier is not sold on the requested interval. */
export const INTERVAL_NOT_OFFERED_REASON = "interval_not_offered";

/** Refused because this environment has no Stripe Price for the plan yet. */
export const PRICE_NOT_CONFIGURED_REASON = "price_not_configured";

/** Stripe's hosted Checkout URL; the client navigates to it. */
export const checkoutResp = z.object({
  url: z.string(),
});

/**
 * Refused because the user already holds an entitling Stripe claim. Buying a
 * second subscription would charge twice for one plan, so the upgrade path is
 * a plan change rather than a new checkout.
 */
export const CHECKOUT_ALREADY_SUBSCRIBED_REASON = "already_subscribed";

/**
 * The session id Stripe puts in the success URL. It is client-supplied and
 * therefore untrusted: the server must confirm the session belongs to the
 * caller's own customer before reading a plan out of it.
 */
export const checkoutRefreshReq = z.object({
  sessionId: z.string(),
});

/**
 * A tier change that has not happened yet. Downgrades take effect at the end
 * of the period the user already paid for, so this is the difference between
 * what they are on and what they are heading to.
 */
export const scheduledChangeResp = z.object({
  tier: z.enum(PAID_TIERS),
  interval: billingInterval,
  effectiveAt: z.string(),
});

/**
 * Live subscription details, present only when the winning claim is Stripe
 * and Stripe answered. Absent means "we could not ask right now" — the plan
 * itself still resolves from stored claims, so the caller shows the tier and
 * omits the management affordances rather than showing an error.
 */
export const planSubscriptionResp = z.object({
  /**
   * The tier this subscription sells, which is NOT always the tier the account
   * currently resolves to. Once a grace period expires the account is on Free
   * while the subscription still says Pro — and "your Pro plan has ended" is
   * only sayable because this field survives the drop.
   */
  tier: z.enum(PAID_TIERS),
  /** How often it renews; null when its price maps to no plan we model. */
  interval: billingInterval.nullable(),
  status: z.enum(SUBSCRIPTION_STATUSES),
  currentPeriodEnd: z.string().nullable(),
  cancelAtPeriodEnd: z.boolean(),
  /**
   * When a failed payment stops conferring the tier — set only while the
   * subscription is `past_due`, null otherwise.
   *
   * Sent because `status: "past_due"` alone cannot tell the client whether the
   * plan is still working. A dunning subscription keeps its tier for the grace
   * period and then does not, and those are opposite things to say to someone.
   */
  graceEndsAt: z.string().nullable(),
});

export const planSubscriptionWithScheduleResp = planSubscriptionResp.extend({
  scheduledChange: scheduledChangeResp.nullable(),
});

/**
 * `interval` is optional for web builds that predate annual billing; the
 * server keeps the current interval when the target tier is sold on it.
 */
export const planChangeReq = z.object({
  tier: z.enum(PAID_TIERS),
  interval: billingInterval.optional(),
});

/** Refused because there is no Stripe subscription to change. */
export const PLAN_NO_SUBSCRIPTION_REASON = "no_active_subscription";

/** Refused because the requested plan — tier and interval — is the one already held. */
export const PLAN_SAME_TIER_REASON = "already_on_tier";

/**
 * Refused because the scheduled change being dropped has already taken effect:
 * the page asking to keep the old plan was loaded before the period ended.
 */
export const PLAN_SCHEDULED_CHANGE_APPLIED_REASON = "scheduled_change_already_applied";

/**
 * An immediate change that needed a charge (a switch to annual billing) was
 * declined, so Stripe held it and the user is still on their old plan.
 */
export const PLAN_CHANGE_PAYMENT_FAILED_REASON = "payment_failed";

/**
 * Like `payment_failed`, except the bank wants the customer to approve the
 * charge. Passing `clientSecret` to `stripe.handleNextAction` runs that
 * approval, and the change applies once the payment succeeds.
 */
export const PLAN_CHANGE_REQUIRES_ACTION_REASON = "payment_requires_action";

export const planChangeRequiresActionResp = z.object({
  error: z.string(),
  reason: z.literal(PLAN_CHANGE_REQUIRES_ACTION_REASON),
  clientSecret: z.string(),
});

export type PlanChangeTiming = "noop" | "immediate" | "period_end";

/**
 * When a change between two paid plans takes effect.
 *
 * Anything that gives the user more — a higher tier, or the same tier paid a
 * year ahead — happens now and is prorated. Anything that gives them less
 * waits for the end of the period they already paid for, so nothing is taken
 * away at the moment they ask. Tier decides first; the interval only matters
 * between two prices of the same tier.
 *
 * Shared with the client so the confirmation dialog promises exactly what the
 * server will do.
 */
export function planChangeTiming(
  from: { tier: PaidTier; interval: BillingInterval },
  to: { tier: PaidTier; interval: BillingInterval },
): PlanChangeTiming {
  const rankDelta = PLAN_TIERS.indexOf(to.tier) - PLAN_TIERS.indexOf(from.tier);
  if (rankDelta > 0) return "immediate";
  if (rankDelta < 0) return "period_end";
  if (from.interval === to.interval) return "noop";
  return to.interval === "year" ? "immediate" : "period_end";
}

/** A Stripe Price as the client shows it. `unitAmount` is in the currency's minor unit (cents). */
export const priceResp = z.object({
  unitAmount: z.number().int(),
  currency: z.string(),
});

/**
 * One tier as it is sold. A null price means the tier is not offered on that
 * interval here — not sold that way, not configured in this environment, or
 * Stripe could not be reached — and the client hides or disables it.
 */
export const catalogEntryResp = z.object({
  tier: z.enum(PLAN_TIERS),
  /** 1x, 5x, 20x — the only size users are shown. Null for free. */
  multiple: z.number().nullable(),
  /** Sold as "effectively unlimited" even though a cap exists. */
  marketedUnlimited: z.boolean(),
  prices: z.object({
    month: priceResp.nullable(),
    year: priceResp.nullable(),
  }),
});

/**
 * A plan granted by hand rather than bought — a launch grant, or a support
 * gesture — and when it ends.
 */
export const planGrantResp = z.object({
  tier: z.enum(PAID_TIERS),
  endsAt: z.string(),
});

export const planResp = z.object({
  tier: z.enum(PLAN_TIERS),
  /** Which source's claim won; null when the tier is free. */
  source: z.enum(SUBSCRIPTION_SOURCES).nullable(),
  /** The window usage is measured over, starting at `periodStart`. */
  allowancePeriod: z.enum(ALLOWANCE_PERIODS),
  /** Share of this period's allowance used, 0–100; null = unlimited. */
  percentUsed: z.number().int().nullable(),
  /** When the allowance resets — the same instant as `periodEnd`. */
  resetsAt: z.string(),
  /** The current tier's multiple; null for free. */
  multiple: z.number().nullable(),
  /** Every tier with its prices, lowest tier first. */
  catalog: z.array(catalogEntryResp),
  /** Set when the plan comes from a hand-made grant with an end date. */
  grant: planGrantResp.nullable(),
  /**
   * The current period's allowance — weekly since the 2026-10 policy, despite the name. null = unlimited.
   * @deprecated Users never see unit counts; kept for web builds that predate `percentUsed`.
   */
  monthlyUnits: z.number().int().nullable(),
  /** @deprecated See `monthlyUnits`. */
  unitsUsed: z.number().int(),
  /** null = unlimited; never negative. @deprecated See `monthlyUnits`. */
  unitsRemaining: z.number().int().nullable(),
  periodStart: z.string(),
  periodEnd: z.string(),
  subscription: planSubscriptionWithScheduleResp.nullable(),
  /**
   * Allowance per tier, null = unlimited.
   * @deprecated Use `catalog`, which names each tier by its multiple.
   */
  tierAllowances: z.record(z.enum(PLAN_TIERS), z.number().int().nullable()),
});

/**
 * The plans as they are sold today, for the signed-out pricing page: the same
 * entries `planResp.catalog` carries, with nobody's plan or usage attached.
 */
export const publicCatalogResp = z.object({
  /** The window allowances are measured over under today's policy. */
  allowancePeriod: z.enum(ALLOWANCE_PERIODS),
  /**
   * When the allowance window in force now ends. Not always a whole period
   * away: a window cut short by a policy change ends where the next begins.
   */
  resetsAt: z.string(),
  /** Every tier with its prices, lowest tier first. */
  catalog: z.array(catalogEntryResp),
});

export type CheckoutReq = z.infer<typeof checkoutReq>;
export type CheckoutResp = z.infer<typeof checkoutResp>;
export type CheckoutRefreshReq = z.infer<typeof checkoutRefreshReq>;
export type PlanSubscriptionResp = z.infer<typeof planSubscriptionWithScheduleResp>;
export type ScheduledChangeResp = z.infer<typeof scheduledChangeResp>;
export type PlanChangeReq = z.infer<typeof planChangeReq>;
export type PriceResp = z.infer<typeof priceResp>;
export type CatalogEntryResp = z.infer<typeof catalogEntryResp>;
export type PlanGrantResp = z.infer<typeof planGrantResp>;
export type PlanResp = z.infer<typeof planResp>;
export type PublicCatalogResp = z.infer<typeof publicCatalogResp>;
export type PlanChangeRequiresActionResp = z.infer<typeof planChangeRequiresActionResp>;
